import React, { createContext, useState, useEffect, useCallback, ReactNode, useContext } from 'react';
import { useAuth } from './AuthProvider';
import { getMyHostProfile } from '../services/HostProfileService';
import { IHostProfile } from '../interfaces/heavyDutyMachinery/IHostProfile';

interface HostProfileContextProps {
  hostProfile: IHostProfile | undefined;
  hasHostProfile: boolean;
  loading: boolean;
  refresh: () => Promise<void>;
}

interface HostProfileProviderProps {
  children: ReactNode;
}

const HostProfileContext = createContext<HostProfileContextProps | undefined>(undefined);

/**
 * Single source of truth for "does the signed-in user have a HostProfile
 * yet?" -- previously fetched independently (and inconsistently) by
 * HeavyDutyMachineryForm, MyHeavyDutyMachineries, and EditProfile. Now every one of
 * those, plus the header nav and the "List your space" button's routing
 * decision, reads from here instead.
 */
export const HostProfileProvider: React.FC<HostProfileProviderProps> = ({ children }) => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [hostProfile, setHostProfile] = useState<IHostProfile | undefined>(undefined);
  const [loading, setLoading] = useState(true);

  // See UserProfileProvider's identical comment: a fresh login flips
  // isAuthenticated synchronously, one render before the effect below gets
  // a chance to re-fetch and reset `loading` -- adjusting state during
  // render closes that gap instead of leaving a stale loading=false/
  // hostProfile=undefined visible for one render.
  const [syncedFor, setSyncedFor] = useState(isAuthenticated);
  if (isAuthenticated !== syncedFor) {
    setSyncedFor(isAuthenticated);
    setLoading(true);
  }

  const refresh = useCallback(async () => {
    if (!isAuthenticated) {
      setHostProfile(undefined);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const profile = await getMyHostProfile();
      setHostProfile(profile);
    } catch {
      setHostProfile(undefined);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  // See UserProfileProvider's identical comment: waiting for AuthProvider's
  // own loading to clear avoids acting on a transient isAuthenticated=false
  // before the real auth check has resolved.
  useEffect(() => {
    if (authLoading) return;
    refresh();
  }, [authLoading, refresh]);

  return (
    <HostProfileContext.Provider value={{ hostProfile, hasHostProfile: !!hostProfile, loading, refresh }}>
      {children}
    </HostProfileContext.Provider>
  );
};

export const useHostProfile = (): HostProfileContextProps => {
  const context = useContext(HostProfileContext);
  if (!context) throw new Error('useHostProfile must be used within a HostProfileProvider');
  return context;
};
